import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchLocations } from "./api/ocpi";
import { StationsMap } from "./components/Map";
import { Sidebar } from "./components/Sidebar";
import { useGeolocation } from "./hooks/useGeolocation";
import { haversineKm, locCoord } from "./utils/format";
import type { OcpiLocation } from "./types/ocpi";

export interface LocationWithDistance {
  location: OcpiLocation;
  distanceKm: number | null;
}

export function App() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const geo = useGeolocation();

  const query = useQuery({
    queryKey: ["ocpi", "locations"],
    queryFn: ({ signal }) => fetchLocations(signal),
  });
  const locations = query.data ?? [];

  const enriched: LocationWithDistance[] = useMemo(() => {
    if (!geo.coords) return locations.map((l) => ({ location: l, distanceKm: null }));
    return locations.map((l) => ({
      location: l,
      distanceKm: haversineKm(geo.coords!, locCoord(l)),
    }));
  }, [locations, geo.coords]);

  const handleLocate = () => {
    if (geo.status === "granted" || geo.status === "prompting") geo.clear();
    else geo.request();
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-white">
      <Sidebar
        items={enriched}
        selectedId={selectedId}
        onSelect={setSelectedId}
        loading={query.isLoading}
        error={query.error as Error | null}
        onRetry={() => query.refetch()}
        userActive={geo.status === "granted"}
        onLocateClick={handleLocate}
        geoBusy={geo.status === "prompting"}
        geoError={geo.status === "denied" ? "geoDenied" : geo.status === "unavailable" ? "geoUnavailable" : null}
      />
      <main className="relative flex-1">
        <StationsMap
          locations={locations}
          selectedId={selectedId}
          onSelect={setSelectedId}
          userCoords={geo.coords}
          onLocateClick={handleLocate}
          locateActive={geo.status === "granted"}
          locateBusy={geo.status === "prompting"}
        />
      </main>
    </div>
  );
}
